import { useEffect, useState } from 'react';
import { AlertTriangle, Plus, Trash2, X } from 'lucide-react';
import type { CategoriaVista, InsumoVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { centavosAPesos, pesosACentavos } from '@shared/centavos';
import {
  etiquetaDePorcion,
  nombreDePorcion,
  porcionesSugeridas,
  revisarPorciones,
  type Porcion,
} from '@shared/porciones';

/**
 * Sprint 63 — Vender un ingrediente en varios tamaños, en una sola pantalla.
 *
 * Reporte del negocio: para vender Picaña en cuatro tamaños había que capturar el
 * ingrediente y cuatro productos, cada uno con su cantidad y su vínculo. Cinco
 * pantallas, cinco lugares donde equivocarse, y de ahí la queja: *"todo quedó
 * desconectado y MUY complejo de administrar"*.
 *
 * Tres decisiones de diseño:
 *
 *  1. **Las porciones vienen propuestas.** Elegir la carne ya llena la tabla con 1,
 *     3/4, 1/2 y 1/4 — sólo falta el precio. Para un pollo que se parte en mitades
 *     propone Entero y Medio; para una lata, sólo Entero.
 *  2. **Se ve el resultado antes de guardar.** "Se van a crear 4 productos: Picaña
 *     1 kg…". Nadie debería descubrir qué hizo un botón después de apretarlo.
 *  3. **Avisa cuando el nombre no concuerda con la cantidad.** En la lista del negocio
 *     venía "Picaña 3/4 kg (500 gramos)" — tres cuartos son 750 g. Ese dedazo descuenta
 *     de menos en cada venta, para siempre, y no se nota en ninguna pantalla.
 */

interface Props {
  abierto: boolean;
  insumos: InsumoVista[];
  categorias: CategoriaVista[];
  onClose: () => void;
  onCreadas: (cuantas: number) => void;
}

export function ModalPorciones({ abierto, insumos, categorias, onClose, onCreadas }: Props) {
  const [insumoId, setInsumoId] = useState<number | null>(null);
  const [categoriaId, setCategoriaId] = useState<number | undefined>(undefined);
  const [porciones, setPorciones] = useState<Porcion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const insumo = insumos.find((i) => i.id === insumoId) ?? null;

  useEffect(() => {
    if (!abierto) return;
    setInsumoId(null);
    setCategoriaId(undefined);
    setPorciones([]);
    setError(null);
  }, [abierto]);

  if (!abierto) return null;

  /** Elegir el ingrediente propone sus porciones habituales. */
  const elegirInsumo = (id: number | null) => {
    setInsumoId(id);
    setError(null);
    const ins = insumos.find((i) => i.id === id);
    setPorciones(ins ? porcionesSugeridas(ins.unidad, ins.fraccionMinima) : []);
  };

  const cambiar = (indice: number, cambio: Partial<Porcion>) => {
    setPorciones((prev) => prev.map((p, i) => (i === indice ? { ...p, ...cambio } : p)));
  };

  const agregar = () => {
    if (!insumo) return;
    const cantidad = insumo.fraccionMinima ?? 1;
    setPorciones((prev) => [
      ...prev,
      { etiqueta: etiquetaDePorcion(cantidad, insumo.unidad), cantidad, precio: 0 },
    ]);
  };

  const problemas = insumo ? revisarPorciones(porciones, insumo.unidad, insumo.fraccionMinima) : [];
  const problemaDe = (indice: number) => problemas.filter((p) => p.indice === indice);
  const hayBloqueo = problemas.some((p) => p.bloquea);
  const puedeGuardar = !!insumo && porciones.length > 0 && !hayBloqueo && !enviando;

  const guardar = async () => {
    if (!insumo) return;
    setEnviando(true);
    setError(null);
    const res = await window.api.productos.crearPorciones({
      insumoId: insumo.id,
      categoriaId,
      porciones: porciones.map((p) => ({
        etiqueta: p.etiqueta.trim(),
        cantidad: p.cantidad,
        precio: p.precio,
      })),
    });
    setEnviando(false);
    if (res.ok) onCreadas(res.data.length);
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Vender un ingrediente en varios tamaños</h2>
            <p className="text-sm text-slate-500">
              Se crea un producto por tamaño, todos descontando del mismo inventario.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="porc-insumo">
                ¿Qué ingrediente? *
              </label>
              <select
                id="porc-insumo"
                className="input"
                value={insumoId ?? ''}
                onChange={(e) => elegirInsumo(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Elige el ingrediente…</option>
                {insumos.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.nombre} — hay {i.stockActual} {i.unidad}
                  </option>
                ))}
              </select>
              {insumos.length === 0 && (
                <p className="mt-1 text-xs text-amber-800">
                  Todavía no hay ingredientes. Captúralos primero en Inventario.
                </p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="porc-cat">
                Categoría
              </label>
              <select
                id="porc-cat"
                className="input"
                value={categoriaId ?? ''}
                onChange={(e) => setCategoriaId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">Sin categoría</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">Se aplica a todos los tamaños.</p>
            </div>
          </div>

          {insumo && (
            <>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Cómo se llama</th>
                      <th className="px-3 py-2 text-right">Lleva ({insumo.unidad})</th>
                      <th className="px-3 py-2 text-right">Precio</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {porciones.map((p, i) => {
                      const suyos = problemaDe(i);
                      const bloquea = suyos.some((x) => x.bloquea);
                      return (
                        <tr key={i} className={bloquea ? 'bg-red-50/50' : undefined}>
                          <td className="px-3 py-2">
                            <input
                              className="input"
                              value={p.etiqueta}
                              aria-label={`Nombre de la porción ${i + 1}`}
                              onChange={(e) => cambiar(i, { etiqueta: e.target.value })}
                            />
                            {suyos.map((x, k) => (
                              <p
                                key={k}
                                className={`mt-1 flex items-start gap-1 text-xs ${
                                  x.bloquea ? 'font-medium text-red-700' : 'text-amber-800'
                                }`}
                              >
                                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                {x.mensaje}
                              </p>
                            ))}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <NumberField
                              min={0}
                              decimals={3}
                              className="input text-right font-mono"
                              value={p.cantidad}
                              ariaLabel={`Cantidad de la porción ${i + 1}`}
                              onChange={(n) => cambiar(i, { cantidad: n })}
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <NumberField
                              min={0}
                              decimals={2}
                              className="input text-right font-mono"
                              value={centavosAPesos(p.precio)}
                              ariaLabel={`Precio de la porción ${i + 1}`}
                              onChange={(n) => cambiar(i, { precio: pesosACentavos(n) })}
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <button
                              type="button"
                              onClick={() => setPorciones((prev) => prev.filter((_, k) => k !== i))}
                              className="flex h-10 w-10 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                              aria-label={`Quitar la porción ${i + 1}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={agregar}
                className="btn-ghost inline-flex min-h-[44px] items-center gap-1 text-sm"
              >
                <Plus className="h-4 w-4" /> Agregar otro tamaño
              </button>

              {/* Lo que va a pasar al apretar el botón, dicho antes de apretarlo. */}
              {porciones.length > 0 && !hayBloqueo && (
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <p className="font-semibold text-emerald-900">
                    Se van a crear {porciones.length}{' '}
                    {porciones.length === 1 ? 'producto' : 'productos'}:
                  </p>
                  <ul className="mt-1 space-y-0.5 text-emerald-800">
                    {porciones.map((p, i) => (
                      <li key={i}>
                        <strong>{nombreDePorcion(insumo.nombre, p.etiqueta)}</strong> — descuenta{' '}
                        {p.cantidad} {insumo.unidad} de {insumo.nombre}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-emerald-700">
                    Todos comparten el mismo inventario: hoy hay {insumo.stockActual}{' '}
                    {insumo.unidad}.
                  </p>
                </div>
              )}
            </>
          )}

          {error && <div className="error-text">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            className="btn-primary min-h-[44px]"
            disabled={!puedeGuardar}
          >
            {enviando
              ? 'Creando…'
              : porciones.length > 0
                ? `Crear ${porciones.length} ${porciones.length === 1 ? 'producto' : 'productos'}`
                : 'Crear productos'}
          </button>
        </div>
      </div>
    </div>
  );
}
